import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
    Box,
    Typography,
    TextField,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Menu,
    MenuItem,
    Card,
    CardContent,
    Chip,
    Divider,
    Stack,
    Breadcrumbs,
    Link,
} from '@mui/material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import axios from 'axios';
import { alpha, useTheme } from '@mui/material/styles';

const defaultColumns = ['todo', 'in-progress', 'done'];

const TaskBoard = () => {
    const { id: projectId } = useParams();
    const navigate = useNavigate();
    const token = useSelector((state) => state.auth.token);
    const [tasks, setTasks] = useState([]);
    const [columns, setColumns] = useState(defaultColumns);
    const [projectName, setProjectName] = useState('');
    const [newTitle, setNewTitle] = useState('');
    const [newColumn, setNewColumn] = useState('');
    const [selectedTask, setSelectedTask] = useState(null);
    const [columnMenuAnchor, setColumnMenuAnchor] = useState(null);
    const [currentColumn, setCurrentColumn] = useState(null);
    const theme = useTheme();

    const groupedTasks = useMemo(() => {
        return columns.reduce((acc, columnKey) => {
            acc[columnKey] = tasks.filter((task) => task.status === columnKey);
            return acc;
        }, {});
    }, [columns, tasks]);

    const fetchTasks = async () => {
        const res = await axios.get(`/api/tasks/project/${projectId}`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        setTasks(res.data);
    };

    const fetchProject = async () => {
        const res = await axios.get(`/api/projects/${projectId}`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        setColumns(res.data.project.taskColumns || defaultColumns);
        setProjectName(res.data.project.projectName || 'Project');
    };

    useEffect(() => {
        fetchTasks();
        fetchProject();
    }, [projectId]);

    const createTask = async () => {
        if (!newTitle.trim()) return;
        await axios.post('/api/tasks', { projectId, title: newTitle }, {
            headers: { Authorization: `Bearer ${token}` },
        });
        setNewTitle('');
        fetchTasks();
    };

    const addColumn = async () => {
        const col = newColumn.trim();
        if (!col) return;
        const updated = [...columns, col];
        await axios.put(`/api/projects/${projectId}/task-columns`, { columns: updated }, {
            headers: { Authorization: `Bearer ${token}` },
        });
        setColumns(updated);
        setNewColumn('');
    };

    const openColumnMenu = (event, col) => {
        setColumnMenuAnchor(event.currentTarget);
        setCurrentColumn(col);
    };

    const closeColumnMenu = () => {
        setColumnMenuAnchor(null);
        setCurrentColumn(null);
    };

    const renameColumn = async () => {
        const name = window.prompt('New column name', currentColumn);
        if (!name) return closeColumnMenu();
        const updated = columns.map((c) => (c === currentColumn ? name : c));
        await axios.put(`/api/projects/${projectId}/task-columns`, { columns: updated }, {
            headers: { Authorization: `Bearer ${token}` },
        });
        setColumns(updated);
        closeColumnMenu();
    };

    const deleteColumn = async () => {
        if (!window.confirm('Delete column?')) return closeColumnMenu();
        const updated = columns.filter((c) => c !== currentColumn);
        await axios.put(`/api/projects/${projectId}/task-columns`, { columns: updated }, {
            headers: { Authorization: `Bearer ${token}` },
        });
        setColumns(updated);
        closeColumnMenu();
    };

    const createTaskInColumn = async (col) => {
        const title = window.prompt('Task title');
        if (!title) return;
        await axios.post('/api/tasks', { projectId, title, status: col }, {
            headers: { Authorization: `Bearer ${token}` },
        });
        fetchTasks();
    };

    const handleDragEnd = async (result) => {
        const { destination, source, draggableId, type } = result;
        if (!destination) return;

        if (type === 'column') {
            const reordered = Array.from(columns);
            const [removed] = reordered.splice(source.index, 1);
            reordered.splice(destination.index, 0, removed);
            await axios.put(`/api/projects/${projectId}/task-columns`, { columns: reordered }, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setColumns(reordered);
            return;
        }

        const status = destination.droppableId;
        await axios.put(
            `/api/tasks/${draggableId}`,
            { status },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        fetchTasks();
    };

    const openDetails = (task) => setSelectedTask(task);
    const closeDetails = () => setSelectedTask(null);

    return (
        <Box
            sx={{
                py: 3,
                px: { xs: 2, md: 4 },
                bgcolor: alpha(theme.palette.background.paper, 0.6),
                minHeight: '100%',
            }}
        >
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems={{ xs: 'flex-start', md: 'center' }}
                justifyContent="space-between"
                sx={{ mb: 3 }}
            >
                <Box>
                    <Stack spacing={1} sx={{ mb: 1 }}>
                        <Button
                            variant="text"
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate(`/dashboard/projects/${projectId}`)}
                            sx={{ alignSelf: 'flex-start', px: 0 }}
                        >
                            Back to project
                        </Button>
                        <Breadcrumbs aria-label="breadcrumb">
                            <Link component={RouterLink} underline="hover" color="inherit" to="/dashboard/projects">
                                Projects
                            </Link>
                            <Link
                                component={RouterLink}
                                underline="hover"
                                color="inherit"
                                to={`/dashboard/projects/${projectId}`}
                            >
                                {projectName || 'Project'}
                            </Link>
                            <Typography color="text.primary">Task Board</Typography>
                        </Breadcrumbs>
                    </Stack>
                    <Typography variant="h4" sx={{ fontWeight: 700 }} gutterBottom>
                        Task Board
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Organize your project workstreams in a familiar board layout inspired by Asana.
                    </Typography>
                </Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} width={{ xs: '100%', md: 'auto' }}>
                    <TextField
                        value={newTitle}
                        onChange={(e) => setNewTitle(e.target.value)}
                        placeholder="Quick add task"
                        size="small"
                        fullWidth
                        sx={{ minWidth: { md: 220 } }}
                    />
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={createTask}
                        sx={{ whiteSpace: 'nowrap' }}
                    >
                        Add Task
                    </Button>
                </Stack>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 3 }}>
                <TextField
                    value={newColumn}
                    onChange={(e) => setNewColumn(e.target.value)}
                    placeholder="New column name"
                    size="small"
                    sx={{ maxWidth: 260 }}
                />
                <Button variant="outlined" onClick={addColumn} startIcon={<AddIcon />}>
                    Add Column
                </Button>
            </Stack>
            <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="all-columns" direction="horizontal" type="column">
                    {(provided) => (
                        <Box
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 2,
                                overflowX: 'auto',
                                pb: 2,
                            }}
                        >
                            {columns.map((col, index) => (
                                <Draggable draggableId={col} index={index} key={col}>
                                    {(colProvided) => (
                                        <Box
                                            ref={colProvided.innerRef}
                                            {...colProvided.draggableProps}
                                            sx={{
                                                width: { xs: '100%', sm: 320 },
                                                flexShrink: 0,
                                            }}
                                        >
                                            <Card
                                                variant="outlined"
                                                sx={{
                                                    borderRadius: 3,
                                                    overflow: 'hidden',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    height: '100%',
                                                    boxShadow: '0px 12px 32px rgba(15, 23, 42, 0.08)',
                                                }}
                                            >
                                                <Box
                                                    {...colProvided.dragHandleProps}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        px: 2,
                                                        py: 1.5,
                                                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                                                        borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                                                    }}
                                                >
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <Typography
                                                            variant="subtitle2"
                                                            sx={{
                                                                textTransform: 'capitalize',
                                                                fontWeight: 700,
                                                                letterSpacing: 0.4,
                                                            }}
                                                        >
                                                            {col.replace('-', ' ')}
                                                        </Typography>
                                                        <Chip
                                                            label={groupedTasks[col]?.length || 0}
                                                            size="small"
                                                            sx={{ fontWeight: 600 }}
                                                        />
                                                    </Stack>
                                                    <IconButton size="small" onClick={(e) => openColumnMenu(e, col)}>
                                                        <MoreVertIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                                <Droppable droppableId={col} type="task">
                                                    {(taskProvided) => (
                                                        <CardContent
                                                            ref={taskProvided.innerRef}
                                                            {...taskProvided.droppableProps}
                                                            sx={{
                                                                flexGrow: 1,
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                gap: 1.5,
                                                                bgcolor: alpha(theme.palette.background.default, 0.65),
                                                            }}
                                                        >
                                                            <Stack spacing={1.5} sx={{ flexGrow: 1 }}>
                                                                {groupedTasks[col]?.map((t, index) => (
                                                                    <Draggable draggableId={t._id} index={index} key={t._id}>
                                                                        {(dragProvided) => (
                                                                            <Card
                                                                                ref={dragProvided.innerRef}
                                                                                {...dragProvided.draggableProps}
                                                                                {...dragProvided.dragHandleProps}
                                                                                variant="outlined"
                                                                                onClick={() => openDetails(t)}
                                                                                sx={{
                                                                                    cursor: 'pointer',
                                                                                    borderRadius: 2,
                                                                                    boxShadow: '0px 4px 16px rgba(15, 23, 42, 0.08)',
                                                                                    transition: 'all 0.2s ease',
                                                                                    '&:hover': {
                                                                                        boxShadow: '0px 10px 24px rgba(15, 23, 42, 0.12)',
                                                                                        transform: 'translateY(-2px)',
                                                                                    },
                                                                                }}
                                                                            >
                                                                                <CardContent sx={{ p: 2 }}>
                                                                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                                                        {t.title}
                                                                                    </Typography>
                                                                                    {t.description && (
                                                                                        <Typography
                                                                                            variant="body2"
                                                                                            color="text.secondary"
                                                                                            sx={{
                                                                                                mt: 0.75,
                                                                                                display: '-webkit-box',
                                                                                                overflow: 'hidden',
                                                                                                WebkitBoxOrient: 'vertical',
                                                                                                WebkitLineClamp: 3,
                                                                                            }}
                                                                                        >
                                                                                            {t.description}
                                                                                        </Typography>
                                                                                    )}
                                                                                </CardContent>
                                                                            </Card>
                                                                        )}
                                                                    </Draggable>
                                                                ))}
                                                                {taskProvided.placeholder}
                                                            </Stack>
                                                            <Divider flexItem sx={{ my: 0.5 }} />
                                                            <Button
                                                                startIcon={<AddIcon />}
                                                                onClick={() => createTaskInColumn(col)}
                                                                sx={{
                                                                    justifyContent: 'flex-start',
                                                                    color: theme.palette.primary.main,
                                                                    fontWeight: 600,
                                                                }}
                                                            >
                                                                Add task
                                                            </Button>
                                                        </CardContent>
                                                    )}
                                                </Droppable>
                                            </Card>
                                        </Box>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </Box>
                    )}
                </Droppable>
            </DragDropContext>

            <Menu anchorEl={columnMenuAnchor} open={Boolean(columnMenuAnchor)} onClose={closeColumnMenu}>
                <MenuItem onClick={renameColumn}>Rename</MenuItem>
                <MenuItem onClick={deleteColumn}>Delete</MenuItem>
            </Menu>

            {/* Task detail dialog */}
            <Dialog open={!!selectedTask} onClose={closeDetails} fullWidth>
                {selectedTask && (
                    <>
                        <DialogTitle>{selectedTask.title}</DialogTitle>
                        <DialogContent>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                {selectedTask.description || 'No description'}
                            </Typography>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={closeDetails}>Close</Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </Box>
    );
};

export default TaskBoard;
